import { useState, useEffect } from 'react';
import { submitBulkInquiry } from '../api/inquiry.api';
import { toast } from './Toast';
import LocationPicker from './LocationPicker';
import { ENDPOINTS, API_URL } from '../config';
import {
    formatPhoneWithPrefix,
    isValidKenyanPhone,
    normalizeKenyanPhone,
    KENYA_PHONE_ERROR,
    KENYA_PHONE_HINT,
    KENYA_PHONE_PLACEHOLDER,
    KENYA_PHONE_PREFIX,
} from '../utils/phone';

const toImg = (path) => {
    if (!path) return '';
    const p = String(path).replace(/\\/g, '/');
    return p.startsWith('http') ? p : `${API_URL}/${p.replace(/^\/+/, '')}`;
};

const INITIAL = {
    companyName: '',
    contactName: '',
    email: '',
    phone: KENYA_PHONE_PREFIX,
    targetDeliveryLocation: '',
    latitude: '',
    longitude: '',
    expectedDeliveryDate: '',
    specialRequirements: '',
};

const inputCls = 'w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors';

export default function BulkInquiryModal({ isOpen, onClose, prefillItems = [], source = 'landing_page' }) {
    const [form, setForm] = useState(INITIAL);
    const [items, setItems] = useState(
        prefillItems.map((i) => ({ ...i, quantity: i.quantity || 1 }))
    );
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [inquiryNumber, setInquiryNumber] = useState('');

    // Brand → product picker
    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState('');
    const [brandProducts, setBrandProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [pickProductId, setPickProductId] = useState('');
    const [pickQty, setPickQty] = useState(1);

    useEffect(() => {
        if (isOpen) {
            setItems(prefillItems.map((i) => ({ ...i, quantity: i.quantity || 1 })));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Load brands when the modal opens.
    useEffect(() => {
        if (!isOpen) return;
        fetch(ENDPOINTS.BRANDS.PUBLIC)
            .then((r) => r.json())
            .then((d) => setBrands(d?.data || []))
            .catch(() => setBrands([]));
    }, [isOpen]);

    const onBrandChange = async (brandId) => {
        setSelectedBrand(brandId);
        setPickProductId('');
        setBrandProducts([]);
        if (!brandId) return;
        setLoadingProducts(true);
        try {
            const r = await fetch(`${ENDPOINTS.PRODUCTS.SUGGEST}?brand=${encodeURIComponent(brandId)}&limit=200`);
            const d = await r.json();
            setBrandProducts(d?.data?.products || []);
        } catch {
            setBrandProducts([]);
        } finally {
            setLoadingProducts(false);
        }
    };

    const addProduct = () => {
        if (!pickProductId) { toast.error('Please select a product'); return; }
        const p = brandProducts.find((bp) => String(bp._id) === String(pickProductId));
        if (!p) { toast.error('Product not found in list'); return; }
        const qty = Math.max(1, Number(pickQty) || 1);
        setItems((prev) => {
            const idx = prev.findIndex((it) => String(it.productId) === String(p._id));
            if (idx !== -1) {
                return prev.map((it, i) => (i === idx ? { ...it, quantity: it.quantity + qty } : it));
            }
            return [
                ...prev,
                {
                    productId: String(p._id),
                    productName: p.name,
                    quantity: qty,
                    unitPrice: typeof p.price === 'number' ? p.price : 0,
                    imageUrl: toImg(p.image),
                },
            ];
        });
        setPickProductId('');
        setPickQty(1);
    };

    const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

    const set = (field) => (e) => {
        const value =
            field === 'phone' ? formatPhoneWithPrefix(e.target.value) : e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleMapLocationSelect = (lat, lng) => {
        setForm((prev) => ({ ...prev, latitude: lat.toString(), longitude: lng.toString() }));
    };

    const updateItemQty = (idx, qty) => {
        setItems((prev) => prev.map((item, i) => i === idx ? { ...item, quantity: Math.max(1, Number(qty)) } : item));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.phone.trim() || form.phone.trim() === KENYA_PHONE_PREFIX) {
            toast.error('Phone number is required');
            return;
        }
        if (!isValidKenyanPhone(form.phone)) {
            toast.error(KENYA_PHONE_ERROR);
            return;
        }
        setSubmitting(true);
        try {
            const res = await submitBulkInquiry({
                companyName: form.companyName,
                contactName: form.contactName,
                email: form.email,
                phone: normalizeKenyanPhone(form.phone),
                targetDeliveryLocation: form.targetDeliveryLocation,
                deliveryCoordinates: form.latitude && form.longitude
                    ? { latitude: parseFloat(form.latitude), longitude: parseFloat(form.longitude) }
                    : undefined,
                expectedDeliveryDate: form.expectedDeliveryDate || undefined,
                specialRequirements: form.specialRequirements || undefined,
                source,
                items: items.length > 0 ? items : undefined,
            });
            if (res.data?.isOk) {
                setInquiryNumber(res.data.data.inquiryNumber);
                setSubmitted(true);
            } else {
                toast.error(res.data?.message || 'Submission failed');
            }
        } catch {
            toast.error('Failed to submit inquiry. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setForm(INITIAL);
        setSubmitted(false);
        setInquiryNumber('');
        setSelectedBrand('');
        setBrandProducts([]);
        setPickProductId('');
        setPickQty(1);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
            <div
                className="bg-card rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col border border-border"
                style={{ maxWidth: 1080, maxHeight: '92vh' }}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/50 flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-foreground">Bulk / Corporate Order Request</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">We'll prepare a custom quote for you</p>
                    </div>
                    <button
                        onClick={handleClose}
                        aria-label="Close dialog"
                        className="w-8 h-8 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                        <i className="ri-close-line text-lg"></i>
                    </button>
                </div>

                {submitted ? (
                    <div className="flex flex-col items-center justify-center px-8 py-12 text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <i className="ri-checkbox-circle-line text-3xl text-green-600"></i>
                        </div>
                        <h3 className="text-xl font-bold text-foreground mb-2">Inquiry Submitted!</h3>
                        <p className="text-muted-foreground mb-1">
                            Your reference number is <strong className="text-foreground">{inquiryNumber}</strong>.
                        </p>
                        <p className="text-muted-foreground text-sm mb-6">
                            Our sales team will contact you within 1–2 business days with a custom quote.
                        </p>
                        <button
                            onClick={handleClose}
                            className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                        {/* Two-column body — single column on mobile, side-by-side on desktop */}
                        <div className="flex-1 overflow-y-auto lg:overflow-hidden">
                            <div className="grid grid-cols-1 lg:grid-cols-2 lg:h-full divide-y lg:divide-y-0 lg:divide-x divide-border">

                                {/* ── Left column: Contact + Products + Special Requirements ── */}
                                <div className="px-6 py-5 space-y-5 lg:overflow-y-auto">
                                    {/* Company & Contact */}
                                    <div>
                                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Company & Contact</h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">Company Name *</label>
                                                <input type="text" required value={form.companyName} onChange={set('companyName')} className={inputCls} placeholder="Acme Corp" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">Contact Person *</label>
                                                <input type="text" required value={form.contactName} onChange={set('contactName')} className={inputCls} placeholder="John Kamau" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">Email Address *</label>
                                                <input type="email" required value={form.email} onChange={set('email')} className={inputCls} placeholder="john@company.com" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">Phone Number *</label>
                                                <input type="tel" required value={form.phone} onChange={set('phone')} className={inputCls} placeholder={KENYA_PHONE_PLACEHOLDER} />
                                                <p className="text-xs text-muted-foreground mt-1">{KENYA_PHONE_HINT}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Products — brand → product picker (add multiple with quantity) */}
                                    <div>
                                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                                            Products{items.length > 0 ? ` (${items.length})` : ''}
                                        </h3>

                                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-3">
                                            <select value={selectedBrand} onChange={(e) => onBrandChange(e.target.value)} className={`${inputCls} sm:col-span-3`}>
                                                <option value="">Select brand…</option>
                                                {brands.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                                            </select>
                                            <select value={pickProductId} onChange={(e) => setPickProductId(e.target.value)} disabled={!selectedBrand || loadingProducts} className={`${inputCls} sm:col-span-5`}>
                                                <option value="">{loadingProducts ? 'Loading…' : (selectedBrand ? 'Select product…' : 'Select brand first')}</option>
                                                {brandProducts.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                                            </select>
                                            <input type="number" min={1} value={pickQty} onChange={(e) => setPickQty(e.target.value)} aria-label="Quantity" className={`${inputCls} sm:col-span-2 text-center px-1`} />
                                            <button type="button" onClick={addProduct} disabled={!pickProductId}
                                                className="sm:col-span-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                                                Add
                                            </button>
                                        </div>

                                        {items.length > 0 ? (
                                            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                                                {items.map((item, i) => (
                                                    <div key={i} className="flex items-center gap-3 p-2.5 bg-muted/50 rounded-lg border border-border">
                                                        <div className="w-10 h-10 flex-shrink-0 bg-background rounded-lg border border-border overflow-hidden">
                                                            {item.imageUrl ? (
                                                                <img src={item.imageUrl} alt={item.productName} className="w-full h-full object-contain p-1" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center">
                                                                    <i className="ri-image-line text-muted-foreground/40 text-base"></i>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-foreground truncate">{item.productName}</p>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                {item.sku && <span className="text-xs text-muted-foreground">{item.sku}</span>}
                                                                {item.unitPrice > 0 && (
                                                                    <span className="text-xs font-semibold text-primary">
                                                                        KES {Number(item.unitPrice).toLocaleString('en-KE')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1 flex-shrink-0">
                                                            <button type="button" aria-label="Decrease quantity" onClick={() => updateItemQty(i, item.quantity - 1)}
                                                                className="w-7 h-7 flex items-center justify-center border border-border rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors text-sm">–</button>
                                                            <input type="number" min={1} value={item.quantity} onChange={(e) => updateItemQty(i, e.target.value)}
                                                                className="w-10 text-center border border-border rounded px-1 py-1 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
                                                            <button type="button" aria-label="Increase quantity" onClick={() => updateItemQty(i, item.quantity + 1)}
                                                                className="w-7 h-7 flex items-center justify-center border border-border rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors text-sm">+</button>
                                                            <button type="button" aria-label="Remove product" onClick={() => removeItem(i)}
                                                                className="w-7 h-7 flex items-center justify-center border border-border rounded text-red-500 hover:bg-red-500/10 transition-colors text-sm ml-1"><i className="ri-delete-bin-line"></i></button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-muted-foreground">Add one or more products above. (Optional — you can also just describe your needs below.)</p>
                                        )}
                                    </div>

                                    {/* Special requirements */}
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">Special Requirements</label>
                                        <textarea
                                            rows={3}
                                            value={form.specialRequirements}
                                            onChange={set('specialRequirements')}
                                            className={`${inputCls} resize-none`}
                                            placeholder="e.g. custom branding, specific packaging, tax exemption…"
                                        />
                                    </div>
                                </div>

                                {/* ── Right column: Logistics + Map ── */}
                                <div className="px-6 py-5 space-y-4 lg:overflow-y-auto">
                                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Logistics</h3>

                                    {/* Text location */}
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">Delivery Location *</label>
                                        <input
                                            type="text"
                                            required
                                            value={form.targetDeliveryLocation}
                                            onChange={set('targetDeliveryLocation')}
                                            className={inputCls}
                                            placeholder="e.g. Nairobi CBD, Westlands"
                                        />
                                    </div>

                                    {/* Map picker */}
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Pin on Map
                                            <span className="ml-1 text-xs font-normal text-muted-foreground">(click to set exact location)</span>
                                        </label>
                                        <LocationPicker
                                            latitude={form.latitude}
                                            longitude={form.longitude}
                                            onLocationSelect={handleMapLocationSelect}
                                            height="210px"
                                        />
                                    </div>

                                    {/* Lat / Lng */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium text-muted-foreground mb-1">Latitude</label>
                                            <input type="text" value={form.latitude} onChange={set('latitude')} className={inputCls} placeholder="e.g. -1.286389" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-muted-foreground mb-1">Longitude</label>
                                            <input type="text" value={form.longitude} onChange={set('longitude')} className={inputCls} placeholder="e.g. 36.817223" />
                                        </div>
                                    </div>

                                    {/* Expected delivery date */}
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">Expected Delivery Date</label>
                                        <input
                                            type="date"
                                            value={form.expectedDeliveryDate}
                                            onChange={set('expectedDeliveryDate')}
                                            min={new Date().toISOString().split('T')[0]}
                                            className={inputCls}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-border bg-muted/50 flex gap-3 justify-end flex-shrink-0">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="px-5 py-2.5 border border-border text-muted-foreground rounded-lg text-sm font-medium hover:bg-muted hover:text-foreground transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {submitting ? 'Submitting…' : 'Submit Inquiry'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
